import React, { useState } from 'react';
import { FileText, Download, Sparkles, Copy, Check } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, NoteCard } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const AISummarizer = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [summary, setSummary] = useState('');
    const [summaryLength, setSummaryLength] = useState('medium'); // brief, medium, detailed
    const [copied, setCopied] = useState(false);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);
            setSummary('');
            toast.success('PDF loaded successfully');
        }
    };

    const extractTextFromPDF = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;

        let fullText = '';
        const totalPages = pdf.numPages;

        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\\n';

            setProgress(Math.round((pageNum / totalPages) * 50));
        }

        return fullText;
    };

    const summarizePDF = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(5);
            setSummary('');

            // Extract text from PDF
            const extractedText = await extractTextFromPDF(file);

            if (!extractedText.trim()) {
                throw new Error('No text found in PDF. The PDF might be scanned or image-based.');
            }

            setProgress(60);

            // Prepare summary request
            const lengthInstructions = {
                brief: 'Provide a brief summary in 2-3 sentences.',
                medium: 'Provide a comprehensive summary in 1-2 paragraphs.',
                detailed: 'Provide a detailed summary with key points and main topics covered.'
            };

            const prompt = `Please summarize the following document. ${lengthInstructions[summaryLength]}\\n\\nDocument:\\n${extractedText.substring(0, 8000)}`;

            setProgress(70);

            // Call AI API (using a simple mock for now - replace with actual AI API)
            const aiSummary = await generateSummary(prompt, summaryLength);

            setProgress(100);
            setSummary(aiSummary);
            toast.success('Summary generated successfully!');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Summarization error:', error);
            toast.error(`Failed to summarize PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const generateSummary = async (prompt, length) => {
        // Mock AI summary - Replace this with actual AI API call
        // Example: Cloudflare AI, OpenAI, or similar

        // Simulated delay
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Mock summary based on length
        const mockSummaries = {
            brief: 'This document discusses key concepts and provides detailed information on the subject matter. It covers important topics and presents valuable insights.',
            medium: 'This document provides a comprehensive overview of the subject matter, covering various aspects in detail. It begins with an introduction to the core concepts, followed by in-depth analysis of key topics. The document presents valuable insights, supported by examples and evidence, making it a useful resource for understanding the subject.',
            detailed: 'This document offers an extensive examination of the subject matter, presenting a thorough analysis across multiple dimensions. The content is structured to provide readers with a comprehensive understanding, starting from foundational concepts and progressing to advanced topics.\\n\\nKey points covered include:\\n• Introduction to core principles and frameworks\\n• Detailed exploration of main themes and concepts\\n• Analysis of practical applications and real-world examples\\n• Discussion of challenges and potential solutions\\n• Conclusions and recommendations for further study\\n\\nThe document demonstrates a well-researched approach, incorporating various perspectives and supporting evidence to substantiate its claims.'
        };

        return mockSummaries[length];
    };

    const copySummary = () => {
        navigator.clipboard.writeText(summary);
        setCopied(true);
        toast.success('Summary copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    const exportSummary = () => {
        const blob = new Blob([summary], { type: 'text/plain' });
        const fileName = file.name.replace(/\\.pdf$/i, '');
        saveAs(blob, `${fileName}_summary.txt`);
        toast.success('Summary exported!');
    };

    return (
        <div>
            <div className="tool-header">
                <div className="tool-icon-wrapper">
                    <Sparkles size={40} color="var(--color-text-primary)" />
                </div>
                <div className="tool-header-content">
                    <h1 className="tool-title">AI Summarizer</h1>
                    <p className="tool-description">
                        Extract and summarize PDF content using AI. Get quick insights from long documents.
                    </p>
                </div>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setSummary(''); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && !summary && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Summary Length
                            </h3>
                            <div style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                                {[
                                    { value: 'brief', label: '📝 Brief', desc: '2-3 sentences' },
                                    { value: 'medium', label: '📄 Medium', desc: '1-2 paragraphs' },
                                    { value: 'detailed', label: '📚 Detailed', desc: 'Comprehensive' }
                                ].map(option => (
                                    <div
                                        key={option.value}
                                        onClick={() => setSummaryLength(option.value)}
                                        style={{
                                            flex: 1,
                                            padding: 'var(--spacing-4)',
                                            border: `2px solid ${summaryLength === option.value ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer',
                                            backgroundColor: summaryLength === option.value ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
                                            transition: 'all 0.2s',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <div style={{ fontWeight: 'bold', marginBottom: 'var(--spacing-1)' }}>{option.label}</div>
                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>{option.desc}</div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
                                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>Generating summary...</span>
                                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{Math.round(progress)}%</span>
                            </div>
                            <ProgressBar value={progress} striped />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button variant="primary" size="lg" onClick={summarizePDF} disabled={isProcessing} loading={isProcessing} fullWidth>
                            <Sparkles size={20} />
                            Generate AI Summary
                        </Button>
                    </div>
                </>
            )}

            {summary && (
                <div style={{ marginTop: 'var(--spacing-6)' }}>
                    <Card>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)' }}>
                                AI Summary
                            </h3>
                            <Badge variant="success">
                                {summaryLength.charAt(0).toUpperCase() + summaryLength.slice(1)}
                            </Badge>
                        </div>

                        <div style={{
                            padding: 'var(--spacing-4)',
                            backgroundColor: 'var(--color-surface-elevated)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--spacing-4)',
                            lineHeight: 'var(--leading-relaxed)',
                            whiteSpace: 'pre-wrap'
                        }}>
                            {summary}
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                            <Button variant="secondary" onClick={copySummary} style={{ flex: 1 }}>
                                {copied ? <Check size={20} /> : <Copy size={20} />}
                                {copied ? 'Copied!' : 'Copy Summary'}
                            </Button>
                            <Button variant="secondary" onClick={exportSummary} style={{ flex: 1 }}>
                                <Download size={20} />
                                Export as Text
                            </Button>
                            <Button variant="ghost" onClick={() => setSummary('')} style={{ flex: 1 }}>
                                Generate New
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            <NoteCard variant="info">
                <strong>Note:</strong> This tool uses AI to generate summaries. For best results, use PDFs with clear text content. Scanned PDFs may not work as well.
            </NoteCard>
        </div>
    );
};

export default AISummarizer;
